/**
 * Lightweight pub/sub bus for raw FrontEndMessage SocketMessageInterface events.
 *
 * MonitoringWebSocket calls `emitWebSocketMessage()` for every decoded message,
 * BEFORE the dashboard's RAF/Map-dedup path collapses many messages into one
 * "latest state per (bank,lift)" dispatch. Dashboard consumers keep using the
 * existing reducer-driven context; the Terminal subscribes here to observe
 * the unsampled stream as the user expects from a diagnostic view.
 *
 * Zero overhead when no listeners are registered — the emit function
 * early-returns before iterating.
 */

import { SocketMessageInterface } from "../../../interfaces/Monitoring.interface";
import { logError } from "../../../utils/devLog";

export type WebSocketMessageListener = (msg: SocketMessageInterface) => void;

const listeners = new Set<WebSocketMessageListener>();

export function subscribeWebSocketMessage(listener: WebSocketMessageListener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function emitWebSocketMessage(msg: SocketMessageInterface): void {
  if (listeners.size === 0) return;
  listeners.forEach((listener) => {
    // Guard each listener so a buggy subscriber can't abort delivery to the rest.
    // Set.forEach swallows nothing — an uncaught throw would skip remaining listeners.
    try {
      listener(msg);
    } catch (err) {
      logError("[webSocketMessageBus] listener threw:", err);
    }
  });
}

export function hasWebSocketListeners(): boolean {
  return listeners.size > 0;
}
